<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";

const props = defineProps<{ groupIdx: number, focusForm: string[] }>();
const emit = defineEmits(["patch"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3StonesSetting.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, the variant is fetched per stone group via
 * getItemVariants(itemIdx, "stones.<groupIdx>.stone_form") instead of a fixed idx.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone form - stones.<groupIdx>.stone_form
 */

const stoneForm = ref({
  "stone_form": {}
} as any)

const getStoneFormData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneForm.value.stone_form = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_form")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneFormData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneFormData(props.groupIdx)
}, {deep: true})

/**
 * form variants (transferred from itemV3StonesSetting.vue's "setting variants")
 */

const active = computed(() => stoneForm.value.stone_form[0])

const variantsList = computed(() => active.value?.values || [])

// minimum amount of values the variant list needs (after focusValueIdx is applied) before
// the component is shown - until then the whole component stays invisible (v-if = false)
const minVariantsToShow = 2

const matchedIdx = computed(() => variantsList.value.findIndex((v: any) => v.match))

/*
 * focus values (transferred from itemV3StonesSetting.vue's "focus values")
 */

const focusValueIdx = props.focusForm || [
  // idx must match one of the stone_form option keys (see locales/*.json)
  "ROUND",
  "PRINCESS",
  "HEART",
  "BAGUETTE"
]

const getFocusValueIdx = computed(() => {
  if (matchedIdx.value !== -1 && !focusValueIdx.includes(variantsList.value[matchedIdx.value].idx)) {
    return [
      variantsList.value[matchedIdx.value].idx,
      ...focusValueIdx.slice(0, -1)
    ]
  }

  return focusValueIdx
})

// only the values that are part of focusValueIdx should ever be shown; if that list is
// larger than 5, the first 5 are shown directly and the rest are hidden behind "more"
const focusVariantsList = computed(() => getFocusValueIdx.value
    .map((idx: string) => variantsList.value.find((v: any) => v.idx === idx))
    .filter((v: any) => !!v))

const variantsLoaded = computed(() => !!active.value && focusVariantsList.value.length >= minVariantsToShow)

const visibleFocusValues = computed(() => focusVariantsList.value.slice(0, 20))

const moreFocusValues = computed(() => focusVariantsList.value.slice(20))

const hasMoreValues = computed(() => moreFocusValues.value.length > 0)

// whether the "more" values (list entry with the right-arrow) have been revealed inline
const showMore = ref(false)

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

</script>

<template>
  <div v-if="variantsLoaded" class="w-full">
    <ul class="flex gap-x-1 flex-wrap mb-2 w-full justify-center ">
      <!-- stone form list - focus values (only values that are part of focusValueIdx) -->
      <li v-for="(variantData, variantIdx) in visibleFocusValues" :key="variantIdx"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl "
          @click="handleValuePatch(active.idx, variantData)"
      >
        <div class="rounded-2xl flex flex-col items-center p-2 hover:bg-[#f3f0e9]"
             :class="{
               'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
               'border-2 border-transparent': !variantData.match
             }"
        >
          <img
              :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_form/300x300/${variantData.idx.toLowerCase()}.webp`"
              :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_form/600x600/${variantData.idx.toLowerCase()}.webp`"
              class="max-w-12 w-full h-auto object-contain"
          >
          <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ variantData.title }}
          </span>
        </div>
      </li>
      <!-- "more" trigger - placed as the last entry of the focus list, looks like another list item -->
      <li v-if="hasMoreValues && !showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = true"
      >
        <div class="h-14 w-14 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 -rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
          Mehr
        </span>
      </li>
      <!-- stone form list - more values (revealed inline once the "more" entry above was clicked) -->
      <template v-if="showMore">
        <li v-for="(variantData, variantIdx) in moreFocusValues" :key="'more-' + variantIdx"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl "
            @click="handleValuePatch(active.idx, variantData)"
        >
          <div class="rounded-2xl flex flex-col items-center p-2 hover:bg-[#f3f0e9]"
               :class="{
                 'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
                 'border-2 border-transparent': !variantData.match
               }"
          >
            <img
                :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_form/300x300/${variantData.idx.toLowerCase()}.webp`"
                :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_form/600x600/${variantData.idx.toLowerCase()}.webp`"
                class="max-w-12 w-full h-auto object-contain"
            >
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
              {{ variantData.title }}
            </span>
          </div>
        </li>
      </template>
      <!-- "less" trigger - placed as the last entry once "more" is revealed, collapses the list again -->
      <li v-if="showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = false"
      >
        <div class="h-14 w-14 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
          Weniger
        </span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
</style>
